/* @jsx h */
import {
  defineComponent,
  inject,
  onMounted,
  provide,
  ref,
  type Ref,
  h,
} from "vue";
import {
  fetchBranding,
  type BrandingProfile,
  type EatsGuideCity,
} from "../../api/client";

interface BrandingContextType {
  branding: Ref<BrandingProfile | null>;
  defaultCity: Ref<string | null>;
  cities: Ref<EatsGuideCity[]>;
  loading: Ref<boolean>;
}

// Key for provide/inject
const BrandingContextKey = Symbol("BrandingContext");

// Provide Component
export const BrandingProvider = defineComponent({
  setup(_, { slots }) {
    const branding = ref<BrandingProfile | null>(null);
    const defaultCity = ref<string | null>(null);
    const cities = ref<EatsGuideCity[]>([]);
    const loading = ref(true);

    const fetchBrandingData = async () => {
      try {
        loading.value = true;
        const response = await fetchBranding();
        branding.value = response.branding;
        defaultCity.value = response.defaultCity.city;
        cities.value = response.branding.cities;
      } catch (error) {
        console.error("Failed to load branding:", error);
        // Fallback to default branding
        branding.value = {
          id: "lemmon-eats",
          name: "Lemmon",
          subtitle: "Eats Guide",
          cities: [
            { slug: "lausanne", name: "Lausanne", country: "CH", flag: "🇨🇭" },
          ],
        };
        defaultCity.value = "lausanne";
        cities.value = branding.value.cities;
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      fetchBrandingData();
    });

    provide(BrandingContextKey, {
      branding,
      defaultCity,
      cities,
      loading,
    });

    return () => {
      // Wait for branding to load before rendering children
      return loading.value ? null : slots.default?.();
    };
  },
});

// Hook to use Branding
export function useBranding(): BrandingContextType {
  const context = inject<BrandingContextType>(BrandingContextKey);
  if (!context) {
    throw new Error("useBranding must be used within a BrandingProvider");
  }
  return context;
}
